/**
 * TeamLeader Dashboard API — aggregation layer that combines data from
 * multiple backend endpoints into dashboard-ready shapes.
 */
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import {
  fetchMyGroups,
  fetchGroupSummaries,
  type TeamLeaderGroup,
  type GroupSummary,
} from "@/lib/api/teamleader";

// Re-export for convenience
export { fetchMyGroups, fetchGroupSummaries, type TeamLeaderGroup, type GroupSummary };
export {
  fetchAssignableTracks,
  fetchAssignableCourses,
  // Flat, whole-catalogue variants for dropdowns that want an array rather than a page.
  fetchAllAssignableTracks,
  fetchAllAssignableCourses,
  broadcastToMyGroups,
  issueCertificate,
  type IssueCertificatePayload,
} from "@/lib/api/teamleader";

// ── Types ────────────────────────────────────────────────────────────────────

export interface TLStudent {
  id: string;
  full_name: string | null;
  email: string;
  status: string;
  groupId: string;
  groupName: string;
  created_at?: string;
  last_login_at?: string;
}

export interface TLGroupWithStudents extends TeamLeaderGroup {
  studentCount: number;
  students: TLStudent[];
  activeCount: number;
  completedCount: number;
  atRiskCount: number;
}

// ── Fetch helpers ────────────────────────────────────────────────────────────

async function fetchGroupStudents(
  groupId: string,
  groupName: string,
  pageNumber = 1,
  pageSize = 1000,
  search = "",
  status = "",
): Promise<{ items: TLStudent[]; totalCount: number }> {
  const params = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });
  if (search) params.set("search", search);
  if (status) params.set("status", status);
  const res = await fetch(`${API_BASE_URL}/Students/by-group/${groupId}?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return { items: [], totalCount: 0 };
  const data = await res.json();
  const rawItems: any[] = data.items || data || [];
  const totalCount = data.totalCount ?? rawItems.length;
  const items = rawItems.map((s) => ({
    id: s.id,
    full_name: s.full_name ?? s.fullName ?? null,
    email: s.email ?? "",
    status: s.status ?? "unknown",
    groupId,
    groupName,
    created_at: s.created_at ?? s.createdAt,
    last_login_at: s.last_login_at ?? s.lastLoginAt,
  }));
  return { items, totalCount };
}

export async function fetchTLGroupsWithStudents(): Promise<TLGroupWithStudents[]> {
  // 1. Fetch groups (full GroupDto, needed for courses/tracks/teamLeader fields) and
  //    group summaries (aggregate counts) in parallel — 2 requests instead of (1 + 4N).
  const [groups, summaries] = await Promise.all([
    fetchMyGroups(),
    fetchGroupSummaries(),
  ]);

  // Index summaries by group id for O(1) lookup.
  const summaryById = new Map<string, GroupSummary>();
  for (const s of summaries) summaryById.set(s.id, s);

  // 2. Fetch the first page of students for each group in parallel — N requests.
  //    These are the actual data loads; the 4 per-group count probes are gone.
  const enriched = await Promise.all(
    groups.map(async (g) => {
      const studentsPage = await fetchGroupStudents(g.id, g.name, 1, 100);
      const summary = summaryById.get(g.id);

      return {
        ...g,
        studentCount: summary?.studentCount ?? studentsPage.totalCount,
        students: studentsPage.items,
        activeCount: summary?.activeCount ?? 0,
        completedCount: summary?.completedCount ?? 0,
        atRiskCount: summary?.atRiskCount ?? 0,
      };
    }),
  );
  return enriched;
}

/**
 * Returns the total unread message count across all channels for the given user.
 */
export async function fetchTLUnreadCount(userId: string): Promise<number> {
  const res = await fetch(`${API_BASE_URL}/Messages/channels?userId=${userId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return 0;
  const channels = await res.json();
  const arr: any[] = Array.isArray(channels) ? channels : channels?.items || [];
  return arr.reduce(
    (sum, ch) => sum + (ch.unread_count ?? ch.unreadCount ?? 0),
    0,
  );
}
